import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useAuth } from "@/hooks/useAuth";
import { useListing } from "@/hooks/useListing";
import { useFavorites } from "@/hooks/useFavorites";
import { ListingDetails } from "@/types/listing";
import { CarDetailsContent } from "@/components/car-listing/CarDetailsContent";
import { useLanguage } from "@/context/LanguageContext";
import { auctionsLabels } from "@/i18n/labels/auctionsLabels";
import { carDetailsLabels } from "@/i18n";
import { NativeCarDetailsSkeleton } from "@/components/native/NativeCarDetailsSkeleton";
import { useNativeSheetOpensExpanded } from "@/lib/native/useNativeSheetOpensExpanded";
import { isGalleryFullscreenOpen } from "@/lib/galleryFullscreenLock";
import { cn } from "@/lib/utils";

type ForYouListingSheetProps = {
  open: boolean;
  listingId: string | null;
  onOpenChange: (open: boolean) => void;
};

const DISMISS_PX = 90;
const DISMISS_VX = 0.65; // px/ms downward

/** Nearly-full mobile sheet (~94dvh) with smooth drag-to-dismiss on web. */
export function ForYouListingSheet({
  open,
  listingId,
  onOpenChange,
}: ForYouListingSheetProps) {
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const { user } = useAuth();
  const isGuest = !user;
  const userId = user?.id ?? "";
  const { language } = useLanguage();
  const fy = auctionsLabels(language).forYou;
  const detailLabels = carDetailsLabels(language);

  const side = isMobile || isNative ? "bottom" : "right";
  const showFloatingBack = true;
  const webBottom = side === "bottom" && !isNative;

  useNativeSheetOpensExpanded(open, isNative && side === "bottom");

  const { getListing, getPublicListing } = useListing();
  const authQuery = getListing(listingId || "", userId);
  const publicQuery = getPublicListing(listingId || "");
  const listingQuery = isGuest ? publicQuery : authQuery;

  const { addFavorite, removeFavorite } = useFavorites(userId);
  const [listing, setListing] = useState<ListingDetails | null>(null);

  // Smooth drag-to-dismiss (web mobile bottom sheet)
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dragY = useRef(0);
  const dragStartY = useRef<number | null>(null);
  const dragLastY = useRef<number | null>(null);
  const dragLastT = useRef<number | null>(null);
  const dragVelocity = useRef(0);
  const draggingRef = useRef(false);
  const dragArmedRef = useRef(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      setDragOffset(0);
      setDragging(false);
      draggingRef.current = false;
      dragArmedRef.current = false;
      dragStartY.current = null;
      dragY.current = 0;
      dragVelocity.current = 0;
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    if (!open || !listingId) {
      setListing(null);
    }
  }, [open, listingId]);

  useEffect(() => {
    if (!open || !listingQuery.data?.listing) return;
    const raw = listingQuery.data.listing as ListingDetails & {
      isGuestView?: boolean;
    };
    setListing({
      ...raw,
      isGuestView: isGuest || raw.isGuestView,
      auctionId: raw.auctionId ?? raw.id,
      isFavorite: raw.isFavorite ?? false,
      isMyListing: raw.isMyListing ?? false,
      sellerId: raw.sellerId ?? "",
      createdById: raw.createdById ?? "",
      bidFee: raw.bidFee ?? null,
      vehicleTest: {
        ...raw.vehicleTest,
        vatCar: Boolean(
          raw.vehicleTest?.vatCar ??
            (raw.vehicleTest as { isVatCar?: boolean } | undefined)?.isVatCar ??
            false,
        ),
      },
    });
  }, [listingQuery.data, isGuest, open]);

  const isLoading =
    open &&
    !!listingId &&
    (listingQuery.isLoading || (!listing && !listingQuery.error));

  const applyDrag = (y: number) => {
    dragY.current = y;
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setDragOffset(dragY.current);
    });
  };

  const endDrag = (dismiss: boolean) => {
    draggingRef.current = false;
    dragArmedRef.current = false;
    dragStartY.current = null;
    dragLastY.current = null;
    dragLastT.current = null;
    setDragging(false);
    if (dismiss) {
      // Keep offset while sheet animates out
      onOpenChange(false);
      window.setTimeout(() => {
        setDragOffset(0);
        dragY.current = 0;
      }, 280);
      return;
    }
    // Spring back
    setDragOffset(0);
    dragY.current = 0;
    dragVelocity.current = 0;
  };

  const getContentScrollTop = () => {
    const root = scrollRef.current;
    if (!root) return 0;
    if (root.scrollTop > 0) return root.scrollTop;
    const inner = root.querySelector(".overflow-y-auto") as HTMLElement | null;
    return inner?.scrollTop ?? 0;
  };

  const onDragPointerDown = (e: React.PointerEvent) => {
    if (!webBottom) return;
    const fromHandle =
      (e.currentTarget as HTMLElement).dataset.sheetHandle === "1";
    if (!fromHandle && getContentScrollTop() > 2) return;

    dragStartY.current = e.clientY;
    dragLastY.current = e.clientY;
    dragLastT.current = performance.now();
    dragVelocity.current = 0;
    // Handle: arm immediately. Content: wait for clear downward move
    dragArmedRef.current = fromHandle;
    draggingRef.current = fromHandle;
    if (fromHandle) {
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      setDragging(true);
    }
  };

  const onDragPointerMove = (e: React.PointerEvent) => {
    if (!webBottom || dragStartY.current == null) return;
    const y = e.clientY;
    const rawDy = y - dragStartY.current;

    // Content pull: only arm after a clear downward drag while at top
    if (!dragArmedRef.current) {
      if (rawDy < -8) {
        // User scrolling into content — abort
        dragStartY.current = null;
        return;
      }
      if (rawDy < 10) return;
      if (getContentScrollTop() > 2) {
        dragStartY.current = null;
        return;
      }
      dragArmedRef.current = true;
      draggingRef.current = true;
      setDragging(true);
      try {
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      } catch {
        // ignore
      }
    }

    const dy = Math.max(0, rawDy);
    const now = performance.now();
    if (dragLastY.current != null && dragLastT.current != null) {
      const dt = now - dragLastT.current;
      if (dt > 0) dragVelocity.current = (y - dragLastY.current) / dt;
    }
    dragLastY.current = y;
    dragLastT.current = now;

    const max =
      typeof window !== "undefined" ? window.innerHeight * 0.92 : 600;
    const resisted =
      dy < max * 0.55 ? dy : max * 0.55 + (dy - max * 0.55) * 0.28;
    applyDrag(resisted);
    if (dragArmedRef.current && dy > 0) e.preventDefault();
  };

  const onDragPointerUp = () => {
    if (!webBottom || dragStartY.current == null) return;
    if (!dragArmedRef.current) {
      dragStartY.current = null;
      return;
    }
    const dy = dragY.current;
    const vx = dragVelocity.current;
    const shouldDismiss = dy > DISMISS_PX || (dy > 36 && vx >= DISMISS_VX);
    endDrag(shouldDismiss);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={sheetRef}
        side={side}
        expandable={isNative && side === "bottom"}
        dismissWhenExpanded={isNative && side === "bottom"}
        overlayClassName="!z-[210]"
        onEscapeKeyDown={(e) => {
          if (
            isGalleryFullscreenOpen() ||
            (typeof document !== "undefined" &&
              document.querySelector("[data-bilenia-gallery-fullscreen]"))
          ) {
            e.preventDefault();
          }
        }}
        style={
          webBottom
            ? {
                transform:
                  dragOffset > 0
                    ? `translate3d(0, ${dragOffset}px, 0)`
                    : undefined,
                transition: dragging
                  ? "none"
                  : "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
                willChange: "transform",
              }
            : undefined
        }
        className={cn(
          "flex flex-col gap-0 !p-0",
          "bg-background",
          "[&>div]:min-h-0 [&>div]:flex-1 [&>div]:!px-0 [&>div]:!pb-0 [&>div]:!pt-0 [&>div]:!mt-0",
          "[&>button]:!hidden",
          side === "bottom" &&
            cn(
              "!z-[211] !inset-x-0 !left-0 !right-0 !top-auto !bottom-0",
              "!rounded-t-[22px] !rounded-b-none !border-0 !outline-none !ring-0",
              "shadow-[0_-12px_40px_rgba(0,0,0,0.28)]",
              isNative
                ? "native-sheet-expandable w-full max-w-none"
                : "!h-[min(94dvh,100%)] !max-h-[94dvh] overflow-hidden",
              "[&>div]:flex [&>div]:min-h-0 [&>div]:flex-1 [&>div]:flex-col [&>div]:overflow-hidden",
              // Native: no outer safe-area pad — bidding bar paints into the inset
              // (outer pad left an empty white band under the bar).
              isNative
                ? "[&>div]:!pb-0"
                : "[&>div]:!pb-[env(safe-area-inset-bottom,0px)]",
            ),
          side === "right" &&
            cn(
              "!fixed !z-[211] !inset-y-0 !right-0 !left-auto !h-[100dvh] !max-h-[100dvh] w-full",
              "!max-w-[min(580px,100vw)] sm:!max-w-[min(580px,100vw)]",
              "!rounded-none !border-0 !border-l-0 !outline-none !ring-0 !p-0 !gap-0 overflow-hidden",
              "[border-left-width:0!important] [border-left-color:transparent]",
              "bg-background shadow-[-16px_0_40px_rgba(0,0,0,0.45)]",
              "[&>div]:flex [&>div]:min-h-0 [&>div]:flex-1 [&>div]:flex-col [&>div]:overflow-hidden [&>div]:!p-0",
            ),
        )}
      >
        <SheetTitle className="sr-only">{fy.fullAdTitle}</SheetTitle>

        {/* Web only — native already has Sheet dragHandle (duplicate pill blocked dismiss). */}
        {side === "bottom" && webBottom && (
          <div
            data-sheet-handle="1"
            className="sticky top-0 z-[5] flex shrink-0 touch-none cursor-grab active:cursor-grabbing flex-col items-center bg-background pt-2 pb-2"
            onPointerDown={onDragPointerDown}
            onPointerMove={onDragPointerMove}
            onPointerUp={onDragPointerUp}
            onPointerCancel={onDragPointerUp}
          >
            <div
              className="w-12 shrink-0 rounded-full bg-muted-foreground/55"
              style={{ height: 4, minHeight: 4 }}
              aria-hidden
            />
            <span className="sr-only">{fy.backToForYou}</span>
          </div>
        )}

        {showFloatingBack && (
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label={fy.backToForYou}
            className={cn(
              "z-[220] inline-flex h-11 w-8 items-center justify-center rounded-xl border-0",
              "bg-background/80 text-foreground ring-1 ring-border backdrop-blur-md",
              "outline-none focus:outline-none focus-visible:ring-sky-400/40",
              "active:scale-95 transition-transform duration-150 touch-manipulation shadow-sm",
              // Native expandable sheet already clears the status bar — don't
              // stack another safe-area offset or the X floats mid-gallery.
              side === "bottom"
                ? isNative
                  ? "absolute top-3 left-3"
                  : "absolute top-[calc(env(safe-area-inset-top,0px)+28px)] left-3"
                : "absolute top-3 left-3",
            )}
          >
            <X className="h-4 w-4 shrink-0" strokeWidth={2.5} />
          </button>
        )}

        <div
          ref={scrollRef}
          className={cn(
            // CarDetailsContent owns internal scroll + contained bidding bar.
            // Outer must be flex height, not a second scroll, or bidding vanishes.
            "flex h-full min-h-0 flex-1 flex-col overflow-hidden",
            webBottom && "touch-pan-y",
          )}
          onPointerDown={
            webBottom
              ? (e) => {
                  // Allow pull-down dismiss when scrolled to top
                  const el = scrollRef.current?.querySelector(
                    ".overflow-y-auto",
                  ) as HTMLElement | null;
                  if (el && el.scrollTop <= 2) onDragPointerDown(e);
                }
              : undefined
          }
          onPointerMove={webBottom ? onDragPointerMove : undefined}
          onPointerUp={webBottom ? onDragPointerUp : undefined}
          onPointerCancel={webBottom ? onDragPointerUp : undefined}
        >
          {isLoading && (
            <div className="min-h-[50dvh] overflow-y-auto">
              {listingId ? (
                <NativeCarDetailsSkeleton listingId={listingId} />
              ) : (
                <div className="flex h-40 items-center justify-center text-muted-foreground">
                  {detailLabels.loading}
                </div>
              )}
            </div>
          )}

          {!isLoading && listingQuery.error && (
            <div className="flex min-h-[40dvh] items-center justify-center p-6 text-center text-muted-foreground">
              {detailLabels.notFound.title}
            </div>
          )}

          {!isLoading && listing && listingId && (
            <CarDetailsContent
              listingId={listing.id}
              listing={listing}
              userId={userId}
              listingStatus={listing.status}
              isGuestMode={!!listing.isGuestView}
              addFavorite={addFavorite}
              removeFavorite={removeFavorite}
              setListing={setListing}
              presentation="overlay"
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
